import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  fetchPlaceCategory,
  fetchPlaceType,
  addPlaceCategoryCriteriaValue,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function PlaceCategoryClassificationCriteriaAddPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const basePath = `/strategy/${businessDomainId}/place-categories/${categoryId}/classification-criteria`;

  const [criterias, setCriterias] = useState([]);
  const [allValues, setAllValues] = useState([]);
  const [criteriaValues, setCriteriaValues] = useState([]);
  const [selectedCriteria, setSelectedCriteria] = useState("");
  const [selectedValue, setSelectedValue] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const category = await fetchPlaceCategory(categoryId);
        const types = category.associatedPlaceTypes || [];
        if (types.length === 0) return;
        const typeDetails = await Promise.all(
          types.map((t) => fetchPlaceType(t.placeTypeId))
        );
        const allCriterias = typeDetails.flatMap((td) => td.classificationCriterias || []);
        const merged = typeDetails.flatMap((td) => td.classificationCriteriaValues || []);
        // 重複除去（同名の分類基準を1つにまとめる）
        const uniqueCriterias = allCriterias.filter(
          (c, i, arr) => arr.findIndex((x) => x.classificationCriteriaName === c.classificationCriteriaName) === i
        );
        setCriterias(uniqueCriterias);
        setAllValues(merged);
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const handleCriteriaChange = (e) => {
    const name = e.target.value;
    setSelectedCriteria(name);
    setSelectedValue("");
    setCriteriaValues(
      allValues.filter((v) => v.classificationCriteria?.classificationCriteriaName === name)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = criteriaValues.find((v) => v.classificationCriteriaValueName === selectedValue);
    if (!value) return;
    try {
      await addPlaceCategoryCriteriaValue(categoryId, value);
      notify.success(t("message.create.success"));
      navigate(basePath, { replace: true });
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("placeCategory.classificationCriteriaValues")}：{t("common.add")}
      </Typography>
      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 480 }}>
        <FormControl fullWidth required sx={{ mb: 3 }}>
          <InputLabel>{t("itCategory.classificationCriteriaName")}</InputLabel>
          <Select value={selectedCriteria} onChange={handleCriteriaChange}
            label={t("itCategory.classificationCriteriaName")}>
            {criterias.map((c) => (
              <MenuItem key={c.classificationCriteriaName} value={c.classificationCriteriaName}>
                {c.classificationCriteriaName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth required sx={{ mb: 3 }} disabled={!selectedCriteria}>
          <InputLabel>{t("itCategory.classificationCriteriaValueName")}</InputLabel>
          <Select value={selectedValue} onChange={(e) => setSelectedValue(e.target.value)}
            label={t("itCategory.classificationCriteriaValueName")}>
            {criteriaValues.map((v) => (
              <MenuItem key={v.classificationCriteriaValueName} value={v.classificationCriteriaValueName}>
                {v.classificationCriteriaValueName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.add")}</Button>
          <Button variant="outlined" onClick={() => navigate(basePath)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
